import { useCallback, useContext, useMemo, useState } from 'react'

import { GenericError } from '@auth0/auth0-react'
import { Flex, HStack, IconButton, Portal, Separator, Spacer, Spinner, Text, VStack } from '@chakra-ui/react'
import { DeleteIcon, HideIcon, ShowIcon } from '@editor/assets/icons'
import { PanelType } from '@editor/config/constants'
import { INFO_PANEL_PADDING } from '@editor/config/styles'
import { UserContext } from '@editor/contexts/Users'
import { EditorPanelItem } from '@editor/interfaces/editor'
import { InspectionGroup as IInspectionGroup } from '@editor/interfaces/inspectionGroup'
import { useInspectionItems } from '@editor/pages/projects/hooks/useInspectionItems'
import { useShapes } from '@editor/pages/projects/hooks/useShapes'
import { ERROR_PROCESS, getCommonErrorMessage } from '@editor/services/ErrorHandler'
import { deleteInspectionGroup } from '@editor/services/InspectionGroup'
import { isInspectionItem } from '@editor/services/InspectionSheet'
import { withPromiseContained } from '@editor/services/Util'
import { useAppDispatch, useAppSelector } from '@editor/store/app'
import { addEditedElementId, setSelectedElementIds, toggleHiddenElementIds } from '@editor/store/tool'
import { difference } from 'lodash'

import { toggleCollapsedPanel } from '../store/editor'
import { patchEditorDisplaySettings } from '../store/editorSettings'
import { patchInspectionGroup, removeInspectionGroup, updateInspectionGroup } from '../store/inspectionGroup'
import { updateInspectionItems } from '../store/inspectionItems'
import { selectisGroupColoring } from '../store/selectors'
import { Tools } from '../tools/setup'
import InspectionGroup from './InspectionGroup'
import CollapsePanel from './components/CollapsePanel'
import { Layer } from './components/Layer'
import useElementsPanel from './hooks/useElementsPanel'
import {
  findItemsByGroupId,
  findParentByCondition,
  getId,
  getItemsByIds,
  getItemsByKeys,
  isElementSelected,
} from './utils'

export const ElementsPanel = () => {
  // Contexts / Hooks
  const { getAccessToken } = useContext(UserContext)
  const { fetchShapes } = useShapes()
  const { fetchInspectionItems } = useInspectionItems()

  // Store
  const dispatch = useAppDispatch()
  const userType = useAppSelector((state) => state.user.userType)
  const project = useAppSelector((state) => state.page.project)
  const inspectionArea = useAppSelector((state) => state.page.inspectionArea)

  const hiddenElementIds = useAppSelector((state) => state.toolUtil.core.hiddenElementIds)
  const selectedElementIds = useAppSelector((state) => state.toolUtil.core.selectedElementIds)

  const draggingItemKey = useAppSelector((state) => state.inspectionGroup.draggingItemKey)
  const inspectionGroups = useAppSelector((state) => state.inspectionGroup.groups)
  const displaySettings = useAppSelector((state) => state.editorSettings.display)
  const isGroupColoring = useAppSelector(selectisGroupColoring)

  const isElementDeleting = useAppSelector((state) => state.editor.isElementDeleting)
  const selectedTool = useAppSelector((state) => state.editor.selectedTool)
  const permissionSet = useAppSelector((state) => state.editor.permissionSet)
  const isBatchSaving = useAppSelector((state) => state.editor.isBatchSaving)
  const collapsedPanel = useAppSelector((state) => state.editor.collapsedPanel)
  const inspectionItems = useAppSelector((state) => state.inspectionItems.items)

  // Permission check
  const isAllowedToModify = permissionSet.MODIFY.includes(userType)

  // Hooks
  const { handleDelete } = useElementsPanel() // for batch operations

  // State for managing element editing (local UI state only)
  const [editingElements, setEditingElements] = useState<Record<string, boolean>>({})

  // Derived
  const currentEditorTool = Tools.find((tool) => tool.key === selectedTool)
  const isInvisible = !difference(selectedElementIds, hiddenElementIds).length
  const isCollapsed = collapsedPanel.includes(PanelType.Elements)
  const unlabeledColor = displaySettings.general.unlabeled_color

  // Initialize all tools
  const toolsObjectsPanel = Tools.map((tool) =>
    tool.hooks?.useElementsPanel ? tool.hooks.useElementsPanel() : undefined,
  ).filter((config) => !!config)

  /**
   * Generate panel items for each tool
   */
  const toolsPanelItems = useMemo((): EditorPanelItem[] => {
    const getItem = (item: EditorPanelItem): EditorPanelItem => ({
      ...item,
      children: (item.children?.map((child) => getItem(child)) || []).concat(item.item ? getPanelItems(item) : []),
      isOrderable: isAllowedToModify ? item.isOrderable : false,
      // Inject edit state for inspection items
      isEditing: editingElements[item.key] || false,
    })

    const getPanelItems = (parent?: EditorPanelItem) => {
      const parents = toolsObjectsPanel
        .map((hook) => {
          if (!hook) return undefined

          let items: EditorPanelItem[] | undefined
          if (hook?.getPanelItems) {
            items = hook.getPanelItems(parent?.item).map((item) => getItem(item))
          }

          return items
        })
        .flat()
        .filter(Boolean) as EditorPanelItem[]

      return parents
    }

    return [
      {
        key: 'unlabeled-elements',
        name: '未分類要素',
        children: getPanelItems(),
        isVirtualContainer: true,
        color: unlabeledColor,
        hideColorPicker: !isGroupColoring,
        textStyle: { fontWeight: 'bold', fontSize: '1.2em' },
      },
    ]
  }, [toolsObjectsPanel, unlabeledColor, isAllowedToModify, isGroupColoring, editingElements])

  /**
   * Memoized list of inspection groups to be displayed.
   * This includes any callback functions since Redux does not allow functions in the store.
   */
  const groupLayers = useMemo(
    (): EditorPanelItem[] =>
      [...inspectionGroups]
        .sort((a, b) => (a?.order || 0) - (b?.order || 0))
        .map((group) => {
          const onDelete = group.isSaving
            ? undefined
            : async () => {
                if (group.isWorking) {
                  // If this is a new group, just remove it from the store
                  dispatch(removeInspectionGroup(group.key))
                } else if (project && inspectionArea) {
                  // If this is an existing group, update state to indicate deletion
                  // and then delete it from the server
                  dispatch(updateInspectionGroup({ key: group.key, isDeleting: true }))

                  await withPromiseContained(async () => {
                    const token = await getAccessToken()
                    if (!token) throw new Error('No access token found')

                    // Delete the group
                    const result = await deleteInspectionGroup(
                      token,
                      project.project_id,
                      inspectionArea.inspection_area_id,
                      (group.item as IInspectionGroup).group_id,
                    )
                    if (!result) throw new Error('Failed to delete inspection group')

                    await Promise.all([
                      fetchShapes(), // Refresh shapes
                      fetchInspectionItems(), // Refresh inspection items
                    ])

                    // Remove it from the store
                    dispatch(removeInspectionGroup(group.key))
                  }).catch((error) => {
                    dispatch(
                      patchInspectionGroup({
                        key: group.key,
                        isDeleting: false,
                        errorMessage:
                          error instanceof GenericError
                            ? error.message
                            : getCommonErrorMessage(ERROR_PROCESS.INSPECTION_GROUP),
                      }),
                    )
                  })
                }
              }

          return {
            ...group,
            children: group.item
              ? findItemsByGroupId((group.item as IInspectionGroup).group_id, toolsPanelItems).sort(
                  (a, b) => (a.order || 0) - (b.order || 0),
                )
              : undefined,
            onDelete: isAllowedToModify ? onDelete : false,
            isOrderable: isAllowedToModify ? group.isOrderable : false,
            hideColorPicker: !isGroupColoring,
          }
        }),
    [
      isGroupColoring,
      isAllowedToModify,
      inspectionGroups,
      project,
      inspectionArea,
      toolsPanelItems,
      fetchShapes,
      fetchInspectionItems,
      getAccessToken,
      dispatch,
    ],
  )

  /**
   * Get the currently dragged item.
   */
  const draggedItem = useMemo(() => {
    if (!draggingItemKey) return null

    return getItemsByKeys([draggingItemKey], toolsPanelItems)[0] || null
  }, [draggingItemKey, toolsPanelItems])

  /**
   * Only non-working items can be batch deleted.
   */
  const canBatchDelete = useMemo(() => {
    const items = getItemsByIds(selectedElementIds, [...groupLayers, ...toolsPanelItems])
    const areAllWorkingItems = items.every((item) => item.isWorking)

    return selectedElementIds.length && !areAllWorkingItems
  }, [groupLayers, toolsPanelItems, selectedElementIds])

  /**
   * Every selected item can be batch hidden/shown.
   */
  const canBatchVisibility = useMemo(() => selectedElementIds.length > 0, [selectedElementIds])

  /**
   * Handle visibility toggle
   */
  const handleToggleVisibility = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()

      dispatch(toggleHiddenElementIds({ ids: selectedElementIds, invisible: !isInvisible }))
    },
    [selectedElementIds, isInvisible, dispatch],
  )

  /**
   * Handle unlabeled layer update (e.g., color change),
   * to be saved into area's data.
   *
   * @param item The unlabeled elements item to be updated
   */
  const handeUnlabeledUpdate = useCallback(
    (item: EditorPanelItem) => {
      // Only allow the unlabeled parent to be modified.
      if (!isAllowedToModify || item.key !== 'unlabeled-elements' || !item.color) return

      dispatch(patchEditorDisplaySettings({ general: { unlabeled_color: item.color } }))
      document.dispatchEvent(new CustomEvent('inspection-area:patch'))
    },
    [isAllowedToModify, dispatch],
  )

  const handleColorChange = useCallback(
    (item: EditorPanelItem, color: string) => {
      // Only allow the unlabeled parent to be modified.
      if (!isAllowedToModify || item.key !== 'unlabeled-elements' || !project || !inspectionArea) return

      dispatch(patchEditorDisplaySettings({ general: { unlabeled_color: color } }))
    },
    [isAllowedToModify, project, inspectionArea, dispatch],
  )

  /**
   * Handle element update (e.g., name change)
   * This function is called when an element is updated.
   * Instead of saving immediately, it dispatches the change to Redux
   * and the batch save mechanism will handle it.
   *
   * @param item The element item to be updated
   */
  const handleElementUpdate = useCallback(
    (item: EditorPanelItem) => {
      if (!isAllowedToModify || !project || !inspectionArea) return

      // Check if we have an InspectionItem to update (either as item.item or item.inspectionItem)
      const inspectionItemId = isInspectionItem(item.item)
        ? item.item.inspection_item_id
        : item.inspectionItem?.inspection_item_id
      if (!inspectionItemId) return

      // If all we want is to edit it, just update the editing state
      if (item.isEditing) {
        setEditingElements((prev) => ({ ...prev, [item.key]: true }))
        return
      }

      // Clear editing state
      setEditingElements((prev) => {
        const newState = { ...prev }
        delete newState[item.key]
        return newState
      })

      // Get the inspection item from the store
      const inspectionItemFromStore = inspectionItems.find((i) => i.inspection_item_id === inspectionItemId)
      if (!inspectionItemFromStore) return

      // Check if the name actually changed
      const originalName = inspectionItemFromStore.part_name
      if (originalName === item.name) return

      // Optimistic update: directly update the name in Redux (like groups pattern)
      dispatch(
        updateInspectionItems([
          {
            inspection_item_id: inspectionItemId,
            part_name: item.name,
          },
        ]),
      )

      // Add inspection_item_id to edited IDs to trigger batch save
      dispatch(addEditedElementId(inspectionItemId))
    },
    [isAllowedToModify, project, inspectionArea, inspectionItems, dispatch],
  )

  /**
   * Handle updates to items in the unlabeled elements layer.
   * Routes to appropriate handler based on item type.
   */
  const handleItemUpdate = useCallback(
    (updatedItem: EditorPanelItem) => {
      // Handle unlabeled container color changes
      if (updatedItem.key === 'unlabeled-elements') {
        void handeUnlabeledUpdate(updatedItem)
      } else {
        // Handle element name changes
        void handleElementUpdate(updatedItem)
      }
    },
    [handeUnlabeledUpdate, handleElementUpdate],
  )

  /**
   * Determine actual items to be dragged from selected list.
   *
   * For the most part, every selected item is counted as a dragged item.
   * But depending on how the selection is done, sometimes the selected IDs added are
   * not the orderable items (eg: ID of planes in a volume instead of the volume itself).
   * So we cannot naively consider the selected IDs as dragged items.
   *
   * We also need to consider the case where some but not all child items are selected,
   * in which case the parent item is _NOT_ to be counted as dragged item.
   *
   * A parent is only counted as dragged item if all of its children are selected.
   */
  const draggedItems = useMemo(() => {
    if (!draggedItem) return []

    // if the dragged items is not selected yet, it won't be in selectedElementIds
    // so we include it here. Event if it does, Set will ignore duplicates.
    const draggedItemKeys = new Set<string>([draggedItem.key])
    const items = getItemsByIds(selectedElementIds, toolsPanelItems)
    items.forEach((item) => {
      // If the items is already an orderable item, add it to the list
      if (item.isOrderable) {
        draggedItemKeys.add(item.key)
        return
      }

      // Otherwise, find its parent and add it if all children are selected
      const parent = findParentByCondition(toolsPanelItems, getId(item), (prt) => prt.isOrderable === true)
      if (!parent) return

      const isParentSelected = isElementSelected(parent, selectedElementIds, inspectionItems, false, currentEditorTool)
      if (isParentSelected) {
        draggedItemKeys.add(parent.key)
      }
    })

    return getItemsByKeys(Array.from(draggedItemKeys), toolsPanelItems)
  }, [selectedElementIds, draggedItem, toolsPanelItems, currentEditorTool, inspectionItems])

  return (
    <VStack flex={1} w="100%" gap={0} minH={0}>
      <Flex
        id="elements-panel"
        data-testid="elements-panel"
        className="panel"
        flex={isCollapsed ? 'unset' : 1}
        minH={0}
      >
        <CollapsePanel
          title="要素一覧"
          onChange={() => dispatch(toggleCollapsedPanel(PanelType.Elements))}
          isCollapsed={isCollapsed}
          headerRight={isBatchSaving ? <Spinner size="xs" ml={2} /> : undefined}
          preCollapse={
            <>
              {/* Batch operation */}
              <HStack
                id="elements-panel-batch-operation"
                w="100%"
                px={2}
                py={1}
                backgroundColor={canBatchDelete ? 'blue.600' : 'gray2.800'}
                borderBottomColor="gray2.900"
                borderBottomWidth={1}
                cursor="pointer"
                onClick={() => {
                  dispatch(setSelectedElementIds([]))
                }}
                position="sticky"
                top="33px"
                left={0}
                zIndex={10}
              >
                <Text flex={1} whiteSpace="nowrap" color={canBatchDelete ? 'white !important' : undefined}>
                  {canBatchDelete ? '選択した要素 (クリックで全選択解除)' : '選択要素なし '}
                </Text>
                <Spacer />
                <IconButton
                  aria-label="hide"
                  opacity={!canBatchVisibility ? 0.5 : 1}
                  color={!canBatchVisibility ? undefined : 'white'}
                  cursor={!canBatchVisibility ? 'not-allowed' : 'pointer'}
                  variant="panel-icon"
                  onClick={handleToggleVisibility}
                >
                  {isInvisible && canBatchVisibility ? <HideIcon /> : <ShowIcon />}
                </IconButton>
                {isAllowedToModify ? (
                  <IconButton
                    aria-label="delete"
                    variant="panel-icon"
                    disabled={!canBatchDelete || isBatchSaving || isElementDeleting}
                    color={!canBatchDelete ? undefined : 'white'}
                    onClick={async (e) => {
                      e.stopPropagation()

                      // Give both group and unlabeled layers as reference.
                      // There will be duplicates, but ID to layer collection will ignore the later ones,
                      // hence toolsPanelItems is added after groupLayers.
                      await handleDelete(selectedElementIds, [...groupLayers, ...toolsPanelItems])
                      dispatch(setSelectedElementIds([]))
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                ) : null}
              </HStack>
            </>
          }
        >
          <InspectionGroup
            groupLayers={groupLayers}
            isAllowedToModify={isAllowedToModify}
            onElementUpdated={handleElementUpdate}
          />

          <Separator borderColor="whiteAlpha.300" borderBottomWidth="3px" mb={1} />

          <VStack w="100%" gap={0} pb={INFO_PANEL_PADDING - 1}>
            {/* Object list */}
            {toolsPanelItems.map((item) => (
              <Layer
                key={item.key}
                item={item}
                depth={-1}
                editMode={item.isEditing}
                isParentWorking={item.hasWorkingItems}
                onUpdated={handleItemUpdate}
                onColorChanged={(color: string) => handleColorChange(item, color)}
              />
            ))}
          </VStack>
        </CollapsePanel>
      </Flex>

      {/* Shadow clone, dummy for drag preview */}
      <Portal>
        {/* When there's only 1, show itself */}
        {draggedItem && draggedItems.length === 1 ? (
          <>
            <Layer key="shadow-clone" item={draggedItem} depth={1} isShadowClone />
            <Layer key="shadow-clone-group" item={draggedItem} depth={1} isShadowClone forGroup />
          </>
        ) : null}
        {/* If there's multiple, show count */}
        {draggedItem && draggedItems.length > 1 ? (
          <Layer
            item={{
              key: 'shadow-clone-multiple',
              name: `選択要素 ${draggedItems.length} 個`,
              // we append all IDs here so the drag 'n drop does not have to calculate it again
              item: { id: draggedItems.map(getId).join('||') },
              isOrderable: true,
            }}
            depth={0}
            isShadowClone
          />
        ) : null}
      </Portal>
      {/* <Spacer flex={!isCollapsed ? 'unset' : 1} /> */}
    </VStack>
  )
}
